import { useCallback, useEffect, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { AnimatePresence, m } from "framer-motion";
import { AlertCircle, CheckCircle2, Loader2, ShoppingCart, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { FiscalTypeCode } from "@/config/fiscal-types";
import {
  applyBuyerPriceFactor,
  BG_VAT_RATE,
  EU_COUNTRIES,
  isReverseChargeBuyer,
  REVERSE_CHARGE_FACTOR,
  withVatPrefix,
} from "@/lib/pricing/buyer-price";
import { splitVatInclusive } from "@/lib/pricing/vat";

/**
 * Billing step (who the invoice is issued to) — 1:1 port of legacy
 * `src/components/billing/billing-step.tsx`. Used standalone (own footer) or
 * embedded in the checkout flow (hideFooter + controlled state).
 */

// ---------------------------------------------------------------------------
// VAT calculation — inlined 1:1 from legacy `src/lib/billing/vat-calculator.ts`
// (`calculateVat`, subscription rules). The shared implementation lives in
// `@pontimed/billing` which is server-only (depends on @pontimed/db / stripe)
// and is not a SPA dependency, so the pure function is duplicated here.
// ---------------------------------------------------------------------------

interface VatInput {
  /** Recipient's country (ISO 2-letter) */
  recipientCountry: string;
  /** Recipient's fiscal type */
  recipientFiscalType: FiscalTypeCode;
  /** Whether recipient has a valid EU VAT number */
  recipientHasVat: boolean;
}

interface VatResult {
  /** VAT rate as percentage (0 or 20) */
  rate: number;
  /** Legal note to print on invoice (null if standard domestic) */
  note: string | null;
  /** 0% because the buyer accounts for the VAT — such a buyer is charged the price net of VAT. */
  reverseCharge: boolean;
}

const EMITTER_COUNTRY = "BG";

/**
 * Calculate VAT rate and legal note for a SUBSCRIPTION invoice.
 *
 * Rules (Appy Solutions Ltd, BG emitter — sale of digital services):
 * - BG domestic (any recipient) → 20% BG VAT
 * - EU cross-border, B2B with valid VAT → 0% reverse charge (Art. 196)
 * - EU cross-border, B2B without VAT → 20% (place of supply = emitter)
 * - EU cross-border, individual (B2C) → 20% (B2C digital services = emitter VAT until OSS threshold)
 * - Non-EU → 0% (export, outside scope of EU VAT)
 */
function calculateVat(input: VatInput): VatResult {
  const country = input.recipientCountry.toUpperCase();
  const isDomestic = country === EMITTER_COUNTRY;
  const isEU = EU_COUNTRIES.has(country);
  const isIndividual = input.recipientFiscalType === "individual";

  // Domestic (Bulgaria) — always 20%
  if (isDomestic) {
    return { rate: BG_VAT_RATE, note: null, reverseCharge: false };
  }

  // Cross-border EU
  if (isEU) {
    // B2B with valid VAT → reverse charge
    if (!isIndividual && input.recipientHasVat) {
      return {
        rate: 0,
        note: "Reverse charge — Art. 196 VAT Directive / Обратно начисляване — чл. 196 Директива за ДДС",
        reverseCharge: true,
      };
    }
    // B2C or B2B without VAT → emitter charges own VAT (BG 20%)
    return { rate: BG_VAT_RATE, note: null, reverseCharge: false };
  }

  // Non-EU — outside scope
  return { rate: 0, note: "Export outside EU — outside scope of VAT", reverseCharge: false };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface BillingProfile {
  id: string;
  profileName: string;
  businessName?: string;
  vatNumber?: string;
  registrationNumber?: string;
  fiscalType: string;
  country: string;
  address: string;
  isDefault: boolean;
  /** What VIES said of `vatNumber` when the server saved or last re-checked it. */
  vatStatus?: "valid" | "invalid" | "unverified" | null;
}

type BillingChoice = "individual" | "business";

export interface BillingFormState {
  businessName: string;
  vatNumber: string;
  registrationNumber: string;
  country: string;
  address: string;
}

/**
 * POST /api/account/billing/profile body for a company typed into this step.
 * The checkout saves it from its own Continue (checkout-step-billing.tsx): with
 * `hideFooter` this step's own save never runs.
 *
 * The VAT number is stored whatever VIES says, as Settings stores it: it is
 * the only fiscal-code field (a company not registered for VAT types its CUI
 * here) and a Bulgarian buyer needs it on the invoice. The server asks VIES on
 * save and stores the answer; only a "valid" one earns reverse charge.
 */
export function companyProfileRequest(form: BillingFormState, opts: { isDefault: boolean }) {
  return {
    profileName: form.businessName || "Business",
    fiscalType: "small_company",
    country: form.country,
    address: form.address,
    businessName: form.businessName,
    vatNumber: form.vatNumber || undefined,
    registrationNumber: form.registrationNumber || undefined,
    isDefault: opts.isDefault,
  };
}

interface BillingStepProps {
  profiles: BillingProfile[];
  profilesLoading: boolean;
  selectedProfileId: string | null;
  onSelectProfile: (id: string | null) => void;
  onProceed: () => void;
  onBack: () => void;
  loading: boolean;
  /** VAT-inclusive list price for this interval. */
  price?: number;
  /**
   * What a reverse-charge company is charged for it — each unit net, as the
   * server prices it (lib/pricing/buyer-price.ts). Without it: `price` / 1.2.
   */
  netPrice?: number;
  /** Hide the proceed button and disabled hint — parent handles CTA */
  hideFooter?: boolean;
  /** Notifies parent whether the form is valid (for external CTA) */
  onValidityChange?: (valid: boolean) => void;
  /** Controlled state — pass these to persist form state across step navigation */
  controlledChoice?: BillingChoice;
  onChoiceChange?: (choice: BillingChoice) => void;
  controlledBusinessId?: string | null;
  onBusinessIdChange?: (id: string | null) => void;
  controlledShowNew?: boolean;
  onShowNewChange?: (show: boolean) => void;
  controlledForm?: BillingFormState;
  onFormChange?: (form: Partial<BillingFormState>) => void;
  /** Show red borders on empty required fields */
  showErrors?: boolean;
}

export function BillingStep({
  profiles,
  profilesLoading,
  selectedProfileId: _selectedProfileId,
  onSelectProfile,
  onProceed,
  loading,
  price,
  netPrice,
  hideFooter,
  onValidityChange,
  controlledChoice,
  onChoiceChange,
  controlledBusinessId,
  onBusinessIdChange,
  controlledShowNew,
  onShowNewChange,
  controlledForm,
  onFormChange,
  showErrors,
}: BillingStepProps) {
  const { t } = useLingui();

  // Use controlled state when provided, else local state
  const [localChoice, setLocalChoice] = useState<BillingChoice>("individual");
  const [localBusinessId, setLocalBusinessId] = useState<string | null>(null);
  const [localShowNew, setLocalShowNew] = useState(false);
  const [localForm, setLocalForm] = useState({
    businessName: "",
    vatNumber: "",
    registrationNumber: "",
    country: "RO",
    address: "",
  });

  const choice = controlledChoice ?? localChoice;
  const setChoice = (v: BillingChoice) => {
    if (onChoiceChange) onChoiceChange(v);
    else setLocalChoice(v);
  };
  const selectedBusinessId = controlledBusinessId ?? localBusinessId;
  const setSelectedBusinessId = (v: string | null) => {
    if (onBusinessIdChange) onBusinessIdChange(v);
    else setLocalBusinessId(v);
  };
  const showNewBusiness = controlledShowNew ?? localShowNew;
  const setShowNewBusiness = (v: boolean) => {
    if (onShowNewChange) onShowNewChange(v);
    else setLocalShowNew(v);
  };
  const form = controlledForm ?? localForm;
  const setForm = (updater: ((prev: typeof localForm) => typeof localForm) | typeof localForm) => {
    if (onFormChange) {
      const newVal = typeof updater === "function" ? updater(form) : updater;
      onFormChange(newVal);
    } else {
      setLocalForm(updater as typeof localForm | ((prev: typeof localForm) => typeof localForm));
    }
  };
  const [vatValid, setVatValid] = useState<boolean | null>(null);
  const [vatChecking, setVatChecking] = useState(false);
  const [vatBusinessName, setVatBusinessName] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [disabledHint, setDisabledHint] = useState(false);

  // VIES validation
  const validateVat = useCallback(
    async (vat: string) => {
      if (!vat || vat.length < 4) {
        setVatValid(null);
        setVatBusinessName(null);
        return;
      }
      setVatChecking(true);
      try {
        const data = await apiFetch.post<{ valid: boolean; name?: string | null }>(
          "/api/billing/validate-vat",
          { vatNumber: withVatPrefix(vat, form.country) },
        );
        setVatValid(data.valid);
        if (data.valid && data.name) {
          setVatBusinessName(data.name);
          if (!form.businessName) {
            const name = data.name;
            setForm((f) => ({ ...f, businessName: name }));
          }
        }
      } catch {
        setVatValid(null);
      } finally {
        setVatChecking(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.businessName, form.country],
  );

  // Save new business profile then proceed
  const handleSaveAndProceed = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const data = await apiFetch.post<{ profile?: { id: string }; id?: string }>(
        "/api/account/billing/profile",
        companyProfileRequest(form, { isDefault: profiles.length === 0 }),
      );
      onSelectProfile(data.profile?.id || data.id || null);
      onProceed();
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body as { error?: string } | null;
        setSaveError(body?.error || t`Could not save profile. Please try again.`);
      } else {
        setSaveError(t`Could not save profile. Please try again.`);
      }
    } finally {
      setSaving(false);
    }
  }, [form, profiles.length, onSelectProfile, onProceed, t]);

  const businessProfiles = profiles.filter((p) => p.fiscalType !== "individual");

  // Form is shown when showNewBusiness OR no saved business profiles
  const isNewBusinessForm = showNewBusiness || businessProfiles.length === 0;

  // VAT the invoice will carry — the server's rule (recipientVat in
  // packages/billing): reverse charge only for a VIES-valid number of the
  // company's own country. A saved company carries the server's VIES answer;
  // a company being typed, this step's live check.
  const savedBusiness =
    choice === "business" && !isNewBusinessForm
      ? businessProfiles.find((p) => p.id === selectedBusinessId)
      : undefined;
  const vatCountry =
    choice === "individual" ? "BG" : savedBusiness ? savedBusiness.country || "RO" : form.country;
  const vatFiscalType: FiscalTypeCode = choice === "individual" ? "individual" : "small_company";
  const vatNumberValid =
    choice === "business" &&
    isReverseChargeBuyer(
      savedBusiness ?? {
        country: form.country,
        fiscalType: vatFiscalType,
        vatNumber: form.vatNumber,
        vatStatus: vatValid === true ? "valid" : null,
      },
    );
  const vatInfo = calculateVat({
    recipientCountry: vatCountry,
    recipientFiscalType: vatFiscalType,
    recipientHasVat: vatNumberValid,
  });
  const vatRate = vatInfo.rate;
  const charged =
    price != null && vatInfo.reverseCharge
      ? (netPrice ?? applyBuyerPriceFactor(price, REVERSE_CHARGE_FACTOR))
      : price;
  const split = charged != null && charged > 0 ? splitVatInclusive(charged, vatRate) : null;

  // Handle proceed
  const handleProceed = useCallback(() => {
    if (choice === "individual") {
      onSelectProfile(null);
      onProceed();
    } else if (selectedBusinessId && !isNewBusinessForm) {
      onSelectProfile(selectedBusinessId);
      onProceed();
    } else {
      void handleSaveAndProceed();
    }
  }, [
    choice,
    selectedBusinessId,
    isNewBusinessForm,
    onSelectProfile,
    onProceed,
    handleSaveAndProceed,
  ]);

  const canProceed =
    !profilesLoading &&
    (choice === "individual" ||
      (choice === "business" && selectedBusinessId && !isNewBusinessForm) ||
      (choice === "business" &&
        isNewBusinessForm &&
        !!form.businessName &&
        !!form.vatNumber &&
        !!form.registrationNumber &&
        !!form.address &&
        !vatChecking));

  // Notify parent of validity changes
  useEffect(() => {
    onValidityChange?.(!!canProceed);
  }, [canProceed, onValidityChange]);

  if (profilesLoading) {
    return (
      <div className="space-y-3 p-5">
        {[1, 2].map((i) => (
          <div key={i} className="bg-muted h-16 animate-pulse rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <m.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4 p-5">
      <p className="text-muted-foreground text-sm">
        <Trans>Choose who the invoice will be issued to.</Trans>
      </p>

      {/* PF / Business toggle */}
      <div className="bg-muted flex rounded-lg p-1">
        <button
          onClick={() => {
            setChoice("individual");
            setShowNewBusiness(false);
          }}
          className={`flex-1 rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
            choice === "individual"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground"
          }`}
        >
          <Trans>Personal (individual)</Trans>
        </button>
        <button
          onClick={() => setChoice("business")}
          className={`flex-1 rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
            choice === "business"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground"
          }`}
        >
          <Trans>Company</Trans>
        </button>
      </div>

      {/* Business section — expandable */}
      <AnimatePresence>
        {choice === "business" && (
          <m.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="space-y-3">
              {/* Saved business profiles */}
              {businessProfiles.length > 0 && !showNewBusiness && (
                <div className="space-y-2">
                  {businessProfiles.map((profile) => (
                    <button
                      key={profile.id}
                      onClick={() => setSelectedBusinessId(profile.id)}
                      className={`w-full rounded-xl border p-3.5 text-left transition-all ${
                        selectedBusinessId === profile.id
                          ? "border-primary/30 bg-primary/5 ring-primary/20 ring-1"
                          : "border-border hover:border-primary/20"
                      }`}
                    >
                      <div className="text-foreground text-sm font-medium">
                        {profile.businessName}
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {profile.vatNumber && `VAT: ${profile.vatNumber} · `}
                        {profile.vatStatus === "valid" && "VIES ✓ · "}
                        {profile.country}
                      </div>
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      setShowNewBusiness(true);
                      setSelectedBusinessId(null);
                    }}
                    className="text-primary text-xs font-medium underline underline-offset-4"
                  >
                    + <Trans>Add new company</Trans>
                  </button>
                </div>
              )}

              {/* New business form — shown if no saved profiles or user clicked "add new" */}
              {(businessProfiles.length === 0 || showNewBusiness) && (
                <div className="space-y-3">
                  {showNewBusiness && businessProfiles.length > 0 && (
                    <button
                      onClick={() => {
                        setShowNewBusiness(false);
                        setSelectedBusinessId(businessProfiles[0]?.id || null);
                      }}
                      className="text-muted-foreground text-xs underline underline-offset-4"
                    >
                      <Trans>Back to saved profiles</Trans>
                    </button>
                  )}

                  {/* Country */}
                  <div>
                    <label className="text-foreground mb-1 block text-xs font-medium">
                      <Trans>Country</Trans>
                    </label>
                    <select
                      value={form.country}
                      onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                      className="bg-background border-border text-foreground focus:ring-primary/30 focus:border-primary w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-inset"
                    >
                      <option value="RO">România</option>
                      <option value="BG">България</option>
                      <option value="DE">Deutschland</option>
                      <option value="FR">France</option>
                      <option value="IT">Italia</option>
                      <option value="HU">Magyarország</option>
                      <option value="EL">Ελλάδα</option>
                      <option value="AT">Österreich</option>
                      <option value="NL">Nederland</option>
                      <option value="PL">Polska</option>
                      <option value="ES">España</option>
                      <option value="HR">Hrvatska</option>
                    </select>
                  </div>

                  {/* VAT Number */}
                  <div>
                    <label className="text-foreground mb-1 block text-xs font-medium">
                      <Trans>VAT Number</Trans> <span className="text-destructive">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={form.vatNumber}
                        onChange={(e) => {
                          setForm((f) => ({ ...f, vatNumber: e.target.value.toUpperCase() }));
                          setVatValid(null);
                        }}
                        onBlur={() => form.vatNumber && validateVat(form.vatNumber)}
                        placeholder={form.country === "RO" ? "RO12345678" : `${form.country}...`}
                        className={`bg-background text-foreground focus:ring-primary/30 focus:border-primary w-full rounded-lg border px-3 py-2 pr-10 text-sm outline-none focus:ring-2 focus:ring-inset ${showErrors && !form.vatNumber ? "border-destructive" : "border-border"}`}
                      />
                      <div className="absolute top-1/2 right-3 -translate-y-1/2">
                        {vatChecking && (
                          <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                        )}
                        {!vatChecking && vatValid === true && (
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        )}
                        {!vatChecking && vatValid === false && (
                          <X className="text-destructive h-4 w-4" />
                        )}
                      </div>
                    </div>
                    {vatValid === true && vatBusinessName && (
                      <p className="mt-1 text-xs text-emerald-600">VIES ✓ {vatBusinessName}</p>
                    )}
                    {vatValid === false && (
                      <p className="text-destructive mt-1 text-xs">
                        <Trans>VAT number not found in EU VIES registry</Trans> —{" "}
                        <Trans>
                          20% VAT will be applied. Verify and correct the number to get 0% reverse
                          charge.
                        </Trans>
                      </p>
                    )}
                  </div>

                  {/* Business name */}
                  <div>
                    <label className="text-foreground mb-1 block text-xs font-medium">
                      <Trans>Company name</Trans> <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.businessName}
                      onChange={(e) => setForm((f) => ({ ...f, businessName: e.target.value }))}
                      className={`bg-background text-foreground focus:ring-primary/30 focus:border-primary w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-inset ${showErrors && !form.businessName ? "border-destructive" : "border-border"}`}
                    />
                  </div>

                  {/* Registration number */}
                  <div>
                    <label className="text-foreground mb-1 block text-xs font-medium">
                      <Trans>Registration number</Trans> <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.registrationNumber}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, registrationNumber: e.target.value }))
                      }
                      placeholder={form.country === "RO" ? "J40/1234/2020" : ""}
                      className={`bg-background text-foreground focus:ring-primary/30 focus:border-primary w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-inset ${showErrors && !form.registrationNumber ? "border-destructive" : "border-border"}`}
                    />
                  </div>

                  {/* Address */}
                  <div>
                    <label className="text-foreground mb-1 block text-xs font-medium">
                      <Trans>Address</Trans> <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.address}
                      onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                      placeholder={t`123 Example St., City`}
                      className={`bg-background text-foreground focus:ring-primary/30 focus:border-primary w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-inset ${showErrors && !form.address ? "border-destructive" : "border-border"}`}
                    />
                  </div>

                  {saveError && (
                    <div className="bg-destructive/10 flex items-center gap-2 rounded-lg p-2.5">
                      <AlertCircle className="text-destructive h-4 w-4" />
                      <p className="text-destructive text-xs">{saveError}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </m.div>
        )}
      </AnimatePresence>

      {/* Invoice info — always visible */}
      <div className="bg-muted/30 rounded-lg border p-3">
        <div className="text-xs font-semibold">
          <Trans>Invoice details</Trans>
        </div>
        <div className="mt-2 space-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              <Trans>VAT rate</Trans>
            </span>
            <span className="font-medium">{vatInfo.rate}%</span>
          </div>
          {vatInfo.note && (
            <p className="text-muted-foreground mt-1 text-[0.625rem] leading-tight italic">
              {vatInfo.note}
            </p>
          )}
          {/* Prices are VAT-inclusive: VAT is extracted from what is charged (as
              the fiscal invoice does), never added on top. A reverse-charge
              company is charged the price net of VAT. */}
          {vatInfo.reverseCharge && split && (
            <p className="text-muted-foreground text-[0.625rem] leading-tight">
              <Trans>VAT-registered EU company: you pay the price without VAT.</Trans>
            </p>
          )}
          {split && (
            <>
              <div className="border-border/50 flex justify-between border-t pt-1.5 font-semibold">
                <span>
                  {vatInfo.rate > 0 ? <Trans>Total (incl. VAT)</Trans> : <Trans>Total</Trans>}
                </span>
                <span className="tabular-nums">€{split.total.toFixed(2)}</span>
              </div>
              {vatInfo.rate > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    <Trans>of which VAT {vatRate}%</Trans>
                  </span>
                  <span className="font-medium tabular-nums">€{split.vat.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  <Trans>Net amount (excl. VAT)</Trans>
                </span>
                <span className="font-medium tabular-nums">€{split.net.toFixed(2)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Proceed button — wrapped in div to catch clicks even when disabled */}
      {!hideFooter && (
        <div
          onClick={() => {
            if (!canProceed && !loading && !saving) {
              setDisabledHint(true);
              setTimeout(() => setDisabledHint(false), 3000);
            }
          }}
        >
          <Button
            onClick={handleProceed}
            disabled={!canProceed || loading || saving}
            className="w-full"
            size="lg"
          >
            {loading || saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ShoppingCart className="mr-2 h-4 w-4" />
            )}
            {loading || saving ? <Trans>Preparing...</Trans> : <Trans>Proceed to payment</Trans>}
          </Button>
        </div>
      )}
      {!hideFooter && disabledHint && (
        <p className="animate-in fade-in text-destructive text-center text-xs">
          {choice === "business" && isNewBusinessForm ? (
            <Trans>Fill in the required fields to continue.</Trans>
          ) : choice === "business" && !selectedBusinessId ? (
            <Trans>Select a company profile or add a new one.</Trans>
          ) : (
            ""
          )}
        </p>
      )}
    </m.div>
  );
}
